import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, AlertCircle, Info } from "lucide-react";

interface Alert {
  severity: "critical" | "warning" | "info";
  title: string;
  message: string;
  source?: string;
}

interface ActiveAlertsModalProps {
  open: boolean;
  onClose: () => void;
  alerts: Alert[];
}

export function ActiveAlertsModal({ open, onClose, alerts }: ActiveAlertsModalProps) {
  const severityConfig = {
    critical: {
      icon: AlertTriangle,
      color: "border-l-red-500 bg-red-50/50",
      badgeVariant: "destructive" as const,
      iconColor: "text-red-600",
      label: "Crítico"
    },
    warning: {
      icon: AlertCircle,
      color: "border-l-amber-500 bg-amber-50/50",
      badgeVariant: "default" as const,
      iconColor: "text-amber-600",
      label: "Atenção"
    },
    info: {
      icon: Info,
      color: "border-l-blue-500 bg-blue-50/50",
      badgeVariant: "secondary" as const,
      iconColor: "text-blue-600",
      label: "Informação"
    }
  };

  // Ordenar por severidade
  const sortedAlerts = [...alerts].sort((a, b) => {
    const order = { critical: 0, warning: 1, info: 2 };
    return order[a.severity] - order[b.severity];
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Alertas Ativos ({alerts.length})
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {sortedAlerts.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <Info className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                <p>Nenhum alerta ativo no momento</p>
              </CardContent>
            </Card>
          ) : (
            sortedAlerts.map((alert, index) => {
              const config = severityConfig[alert.severity];
              const Icon = config.icon;
              
              return (
                <Card key={index} className={`border-l-4 ${config.color}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg bg-background ${config.iconColor}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-semibold text-sm">{alert.title}</h4>
                          <Badge variant={config.badgeVariant} className="text-xs shrink-0">
                            {config.label}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {alert.message}
                        </p>
                        
                        {alert.source && (
                          <Badge variant="outline" className="text-xs">
                            Fonte: {alert.source}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
